import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Users,
  Trophy,
  Briefcase,
  Inbox,
  Plus,
  Trash2,
  Pencil,
  X,
  Check,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DEPARTMENTS } from "@/lib/departments";

const TABS = [
  { key: "team", label: "Team", icon: Users },
  { key: "clients", label: "Clients", icon: Trophy },
  { key: "services", label: "Services", icon: Briefcase },
  { key: "contact", label: "Inquiries", icon: Inbox },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("team");
  const [dept, setDept] = useState("mens-football");
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // item or null for "new"

  const accent = DEPARTMENTS[dept]?.accent || "#fff";

  function logoutNow() {
    api.post("/auth/logout").catch(() => {});
    logout();
    navigate("/admin/login");
  }

  async function loadList() {
    setLoading(true);
    try {
      if (tab === "contact") {
        const r = await api.get("/admin/contact");
        setContacts(r.data);
      } else {
        const r = await api.get(`/${tab}`, { params: { department: dept } });
        setItems(r.data);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dept]);

  async function onDelete(id) {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${tab}/${id}`);
      toast.success("Deleted");
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function onMarkRead(id) {
    try {
      await api.put(`/admin/contact/${id}/read`);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 p-6 flex flex-col">
        <div className="font-display text-xl uppercase tracking-wide mb-1">CSG</div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">Admin Console</div>

        <nav className="mt-10 space-y-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => {
                setTab(key);
                setEditing(null);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm rounded transition-colors ${
                tab === key
                  ? "bg-white text-black"
                  : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>

        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="text-xs text-white/40 mb-2">{user?.email}</div>
          <button
            onClick={logoutNow}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded"
          >
            <LogOut size={14} /> Sign out
          </button>
          <Link
            to="/"
            target="_blank"
            className="mt-2 w-full inline-flex items-center gap-2 px-3 py-2 text-sm text-white/50 hover:text-white"
          >
            <ExternalLink size={14} /> View site
          </Link>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 md:p-12 overflow-x-auto">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl uppercase">
              {TABS.find((t) => t.key === tab).label}
            </h1>
            <p className="text-sm text-white/50 mt-1">
              {tab === "contact"
                ? "All inquiries across every division."
                : `Managing ${DEPARTMENTS[dept].name}`}
            </p>
          </div>

          {tab !== "contact" && (
            <button
              onClick={() => setEditing({ department: dept })}
              data-testid="admin-create-new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium hover:bg-white/90 rounded"
            >
              <Plus size={16} /> New
            </button>
          )}
        </header>

        {tab !== "contact" && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {Object.values(DEPARTMENTS).map((d) => (
              <button
                key={d.slug}
                data-testid={`admin-dept-${d.slug}`}
                onClick={() => setDept(d.slug)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border rounded transition-colors ${
                  dept === d.slug
                    ? "border-white text-white bg-white/10"
                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
                style={dept === d.slug ? { borderColor: d.accent, color: d.accent } : {}}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-white/40">Loading…</div>
        ) : tab === "contact" ? (
          <ContactsTable contacts={contacts} onMarkRead={onMarkRead} onDelete={onDelete} />
        ) : (
          <ItemsTable
            tab={tab}
            items={items}
            accent={accent}
            onEdit={setEditing}
            onDelete={onDelete}
          />
        )}

        {editing && (
          <EditorModal
            tab={tab}
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              loadList();
            }}
          />
        )}
      </main>
    </div>
  );
}

function ItemsTable({ tab, items, accent, onEdit, onDelete }) {
  if (items.length === 0)
    return <div className="text-white/40">No items yet. Click "New" to add one.</div>;
  return (
    <div className="border border-white/10 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-white/50 text-left uppercase text-[10px] tracking-[0.2em]">
          <tr>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">
              {tab === "team" ? "Role" : tab === "clients" ? "Position" : "Description"}
            </th>
            <th className="px-4 py-3 w-32">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-white/5 hover:bg-white/[0.03]">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {it.photo_url && (
                    <img src={it.photo_url} alt="" className="w-9 h-9 rounded object-cover" />
                  )}
                  <span className="font-medium" style={{ color: accent }}>
                    {it.name || it.title}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-white/70">
                {tab === "services"
                  ? (it.description || "").slice(0, 90)
                  : it.role || it.position}
                {tab === "clients" && it.team ? ` — ${it.team}` : ""}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(it)}
                    data-testid={`admin-edit-${it.id}`}
                    className="p-1.5 border border-white/15 rounded hover:border-white text-white/70 hover:text-white"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => onDelete(it.id)}
                    data-testid={`admin-delete-${it.id}`}
                    className="p-1.5 border border-white/15 rounded hover:border-red-500 text-white/70 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsTable({ contacts, onMarkRead, onDelete }) {
  if (contacts.length === 0) return <div className="text-white/40">No inquiries yet.</div>;
  return (
    <div className="border border-white/10 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-white/50 text-left uppercase text-[10px] tracking-[0.2em]">
          <tr>
            <th className="px-4 py-3">From</th>
            <th className="px-4 py-3">Subject</th>
            <th className="px-4 py-3">Division</th>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr key={c.id} className={`border-t border-white/5 ${c.read ? "opacity-60" : ""}`}>
              <td className="px-4 py-3">
                <div className="font-medium">{c.name}</div>
                <div className="text-white/50 text-xs">{c.email}</div>
                {c.phone && <div className="text-white/40 text-xs">{c.phone}</div>}
              </td>
              <td className="px-4 py-3">
                <div className="font-medium">{c.subject}</div>
                <div className="text-white/60 text-xs mt-1 max-w-md line-clamp-2">{c.message}</div>
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-[0.2em]">
                {DEPARTMENTS[c.department]?.name || c.department}
              </td>
              <td className="px-4 py-3 text-white/50 text-xs">
                {new Date(c.created_at).toLocaleString()}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  {!c.read && (
                    <button
                      onClick={() => onMarkRead(c.id)}
                      data-testid={`admin-contact-read-${c.id}`}
                      className="p-1.5 border border-white/15 rounded hover:border-white text-white/70 hover:text-white"
                      title="Mark read"
                    >
                      <Check size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(c.id)}
                    data-testid={`admin-contact-delete-${c.id}`}
                    className="p-1.5 border border-white/15 rounded hover:border-red-500 text-white/70 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FIELDS = {
  team: [
    { name: "name", label: "Name", required: true },
    { name: "role", label: "Role", required: true },
    { name: "bio", label: "Bio", type: "textarea" },
    { name: "photo_url", label: "Photo URL" },
    { name: "email", label: "Email" },
    { name: "linkedin", label: "LinkedIn URL" },
    { name: "order", label: "Order", type: "number" },
  ],
  clients: [
    { name: "name", label: "Name", required: true },
    { name: "position", label: "Position", required: true },
    { name: "team", label: "Team / Club" },
    { name: "nationality", label: "Nationality" },
    { name: "photo_url", label: "Photo URL" },
    { name: "achievements", label: "Achievements (comma-separated)", type: "csv" },
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "order", label: "Order", type: "number" },
  ],
  services: [
    { name: "title", label: "Title", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "icon", label: "Icon (handshake, file-signature, sparkles, arrow-right-left, megaphone, compass, trophy)" },
    { name: "order", label: "Order", type: "number" },
  ],
};

function EditorModal({ tab, initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const fields = FIELDS[tab];
  const [form, setForm] = useState(() => {
    const base = { department: initial.department || "mens-football" };
    fields.forEach((f) => {
      const v = initial[f.name];
      if (f.type === "csv") base[f.name] = Array.isArray(v) ? v.join(", ") : (v || "");
      else if (f.type === "checkbox") base[f.name] = !!v;
      else if (f.type === "number") base[f.name] = v ?? 0;
      else base[f.name] = v ?? "";
    });
    return base;
  });
  const [saving, setSaving] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "csv") {
        payload[f.name] = (form[f.name] || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      } else if (f.type === "number") {
        payload[f.name] = Number(form[f.name]) || 0;
      }
    });
    try {
      if (isNew) await api.post(`/admin/${tab}`, payload);
      else await api.put(`/admin/${tab}/${initial.id}`, payload);
      toast.success(isNew ? "Created" : "Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 rounded w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="font-display text-2xl uppercase">
            {isNew ? "New" : "Edit"} {tab}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="editor-close">
            <X size={20} />
          </button>
        </header>
        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="admin-editor-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
              Division
            </label>
            <select
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/15 p-3 text-sm focus:border-white outline-none"
            >
              {Object.values(DEPARTMENTS).map((d) => (
                <option key={d.slug} value={d.slug}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                {f.label} {f.required && "*"}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  rows={4}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required}
                  className="w-full bg-transparent border border-white/15 p-3 text-sm focus:border-white outline-none resize-none"
                />
              ) : f.type === "checkbox" ? (
                <input
                  type="checkbox"
                  checked={!!form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                  className="w-5 h-5"
                />
              ) : (
                <input
                  type={f.type === "number" ? "number" : "text"}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required}
                  className="w-full bg-transparent border border-white/15 p-3 text-sm focus:border-white outline-none"
                />
              )}
            </div>
          ))}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              data-testid="editor-save"
              disabled={saving}
              className="px-6 py-2.5 bg-white text-black font-medium text-sm hover:bg-white/90 disabled:opacity-50 rounded"
            >
              {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-white/15 text-sm text-white/70 hover:border-white hover:text-white rounded"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
